import React, { useState, useEffect } from 'react';
import { Bell, Search, User, LogOut, CheckCircle, AlertCircle, UserCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { aicisSupabase, abvigSupabase } from '../../services/supabaseClient';
import { formatDistanceToNow } from 'date-fns';
import { useProfile } from '../../context/ProfileContext';
import { useAuth } from '../../context/AuthContext';
import ProfileModal from '../UI/ProfileModal';
import toast from 'react-hot-toast';

export default function Header() {
  const { profile, greeting } = useProfile();
  const { logout } = useAuth();
  const [showProfile, setShowProfile] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch real notifications from Supabase
  useEffect(() => {
    fetchNotifications();
    
    const subscription = aicisSupabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'registrations'
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'speakers_forms'
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions'
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const allNotifications = [];

      // Fetch recent registrations
      const { data: registrations, error: regError } = await aicisSupabase
        .from('registrations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (!regError && registrations) {
        registrations.forEach(reg => {
          allNotifications.push({
            id: `reg-${reg.id}`,
            type: 'registration',
            title: 'New Diplomatic Sign In',
            message: `${reg.name || 'Someone'} has signed in`,
            time: formatDistanceToNow(new Date(reg.created_at), { addSuffix: true }),
            read: reg.status !== 'pending',
            link: '/aicis/diplomatic-signin',
            createdAt: reg.created_at
          });
        });
      }

      // Fetch recent speakers
      const { data: speakers, error: speakerError } = await aicisSupabase
        .from('speakers_forms')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (!speakerError && speakers) {
        speakers.forEach(speaker => {
          allNotifications.push({
            id: `speaker-${speaker.id}`,
            type: 'speaker',
            title: 'New Speaker Application',
            message: `${speaker.full_name || 'Someone'} applied to speak`,
            time: formatDistanceToNow(new Date(speaker.created_at), { addSuffix: true }),
            read: speaker.status !== 'pending',
            link: '/aicis/speakers',
            createdAt: speaker.created_at
          });
        });
      }

      // Fetch recent transactions
      const { data: transactions, error: transError } = await aicisSupabase
        .from('transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (!transError && transactions) {
        transactions.forEach(trans => {
          allNotifications.push({
            id: `trans-${trans.id}`,
            type: 'payment',
            title: 'New Event Registration',
            message: `Payment of ${trans.amount || '0'} from ${trans.customer_name || 'Someone'}`,
            time: formatDistanceToNow(new Date(trans.created_at), { addSuffix: true }),
            read: trans.status !== 'pending',
            link: '/aicis/event-registration',
            createdAt: trans.created_at
          });
        });
      }

      // Fetch recent contact messages
      const { data: messages, error: msgError } = await abvigSupabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);

      if (!msgError && messages) {
        messages.forEach(msg => {
          allNotifications.push({
            id: `msg-${msg.id}`,
            type: 'contact',
            title: 'New Contact Message',
            message: `Message from ${msg.email || 'Someone'}`,
            time: formatDistanceToNow(new Date(msg.created_at), { addSuffix: true }),
            read: msg.status !== 'pending',
            link: '/abvig/contact-messages',
            createdAt: msg.created_at
          });
        });
      }

      allNotifications.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      const topNotifications = allNotifications.slice(0, 10);
      setNotifications(topNotifications);
      setUnreadCount(topNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (notification.type === 'registration' && notification.id.startsWith('reg-')) {
        const actualId = notification.id.replace('reg-', '');
        await aicisSupabase
          .from('registrations')
          .update({ status: 'viewed' })
          .eq('id', actualId);
      } else if (notification.type === 'speaker' && notification.id.startsWith('speaker-')) {
        const actualId = notification.id.replace('speaker-', '');
        await aicisSupabase
          .from('speakers_forms')
          .update({ status: 'viewed' })
          .eq('id', actualId);
      } else if (notification.type === 'payment' && notification.id.startsWith('trans-')) {
        const actualId = notification.id.replace('trans-', '');
        await aicisSupabase
          .from('transactions')
          .update({ status: 'viewed' })
          .eq('id', actualId);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }

    const updatedNotifications = notifications.map(n =>
      n.id === notification.id ? { ...n, read: true } : n
    );
    setNotifications(updatedNotifications);
    setUnreadCount(updatedNotifications.filter(n => !n.read).length);

    if (notification.link) {
      navigate(notification.link);
    }
    setShowNotifications(false);
  };

  const handleMarkAllAsRead = async () => {
    try {
      const pendingNotifications = notifications.filter(n => !n.read);
      
      for (const notification of pendingNotifications) {
        if (notification.type === 'registration' && notification.id.startsWith('reg-')) {
          const actualId = notification.id.replace('reg-', '');
          await aicisSupabase
            .from('registrations')
            .update({ status: 'viewed' })
            .eq('id', actualId);
        } else if (notification.type === 'speaker' && notification.id.startsWith('speaker-')) {
          const actualId = notification.id.replace('speaker-', '');
          await aicisSupabase
            .from('speakers_forms')
            .update({ status: 'viewed' })
            .eq('id', actualId);
        } else if (notification.type === 'payment' && notification.id.startsWith('trans-')) {
          const actualId = notification.id.replace('trans-', '');
          await aicisSupabase
            .from('transactions')
            .update({ status: 'viewed' })
            .eq('id', actualId);
        }
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }

    const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updatedNotifications);
    setUnreadCount(0);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'registration':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'speaker':
        return <User className="w-4 h-4 text-purple-500" />;
      case 'payment':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'contact':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getInitials = () => {
    if (profile?.name) {
      return profile.name.charAt(0).toUpperCase();
    }
    return 'A';
  };

  const getUserDisplayName = () => {
    if (profile?.name) {
      return profile.name;
    }
    return 'Admin';
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            {/* Mobile: Show title instead of sidebar toggle since sidebar has hamburger */}
            <div className="lg:hidden flex items-center">
              <h1 className="text-lg font-bold text-gray-800 ml-10 sm:ml-0">Dashboard</h1>
            </div>
            
            {/* Desktop Search */}
            <div className="hidden sm:flex relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Mobile Search Toggle */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="sm:hidden p-2 text-gray-400 hover:text-gray-600"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Mobile Search Input */}
            {searchOpen && (
              <div className="sm:hidden absolute left-0 right-0 top-full mt-1 px-4 bg-white border-b border-gray-200 py-2 z-40">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                    autoFocus
                  />
                </div>
              </div>
            )}

            {/* Greeting - hidden on mobile */}
            <div className="hidden lg:block ml-6">
              <p className="text-sm text-gray-600">
                {greeting}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
              >
                <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 block h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-red-500 text-white text-[10px] sm:text-xs font-bold flex items-center justify-center ring-2 ring-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-[90vw] sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Mark all as read
                      </button>
                    )}
                  </div>
                  
                  <div className="flex-1 overflow-y-auto">
                    {loading ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
                        <p className="text-sm text-gray-500 mt-2">Loading...</p>
                      </div>
                    ) : notifications.length > 0 ? (
                      notifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`flex items-start space-x-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0 ${
                            !notification.read ? 'bg-blue-50 hover:bg-blue-100' : ''
                          }`}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {getNotificationIcon(notification.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-0.5">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No notifications</p>
                      </div>
                    )}
                  </div>

                  <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <button
                      onClick={() => setShowNotifications(false)}
                      className="w-full text-center text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="flex items-center space-x-2 p-1 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-medium text-xs sm:text-sm overflow-hidden">
                  {profile?.avatar ? (
                    <img src={profile.avatar} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    getInitials()
                  )}
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-700 hidden xs:block">
                  {getUserDisplayName()}
                </span>
              </button>

              {showProfile && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{getUserDisplayName()}</p>
                    {profile?.email && (
                      <p className="text-xs text-gray-500 truncate">{profile.email}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{greeting}</p>
                  </div>
                  <button 
                    onClick={() => {
                      setShowProfile(false);
                      setShowProfileModal(true);
                    }}
                    className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <UserCircle className="w-4 h-4 mr-2" />
                    Edit Profile
                  </button>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button 
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      <ProfileModal 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)} 
      />
    </>
  );
}